import { Link, useParams } from 'react-router-dom'
import { canWriteArea } from '@wf/shared'
import { ApiError } from '../api/client'
import { useAuth } from '../app/auth'
import { useCustomer } from '../customers/useCustomers'
import { useCreateInvoice, useOrder, useOrderInvoice, useStornoInvoice } from '../orders/useOrders'
import { useSettings } from '../settings/useSettings'
import { BillingDocument, liveDocModel, snapshotDocModel } from './BillingDocument'

/**
 * Rechnungs-Vorschau, -Festschreibung, -Druck & -Storno (Route /print/invoice/:id, auth-gated, ohne App-Shell).
 *
 * - Solange der Auftrag KEINE aktive Rechnung hat (`invNo == null`): unverbindlicher ENTWURF, live aus dem
 *   aktuellen Positionsbestand gerendert, beliebig oft öffenbar, deutlich markiert (Wasserzeichen + Banner).
 *   „Rechnung festschreiben" friert den GoBD-Beleg-Snapshot ein und vergibt die §14-Nummer (einmalig).
 * - Ab Festschreibung wird die FINALE Rechnung aus dem eingefrorenen Snapshot gerendert (nicht mehr live) und
 *   ist beliebig oft druckbar. „Rechnung stornieren" legt eine Stornorechnung an und öffnet den Auftrag wieder
 *   zur Korrektur — danach ist der Entwurf zurück und ein erneutes Festschreiben (neue Nummer) möglich.
 */
export function InvoicePrintPage() {
  const { id: idParam } = useParams()
  const id = Number(idParam)
  const orderQuery = useOrder(id)
  const order = orderQuery.data?.order ?? null
  const invoiced = order?.invNo != null
  const invoiceQuery = useOrderInvoice(id, invoiced)
  const customerQuery = useCustomer(order?.customerId ?? null)
  const settingsQuery = useSettings()
  const { user } = useAuth()
  const createInvoice = useCreateInvoice(id)
  const storno = useStornoInvoice(id)

  if (orderQuery.isLoading || settingsQuery.isLoading || (invoiced && invoiceQuery.isLoading)) {
    return <div className="muted">Rechnung wird geladen…</div>
  }
  if (orderQuery.error || !order) {
    return (
      <div className="error" role="alert">
        Auftrag konnte nicht geladen werden.
      </div>
    )
  }

  const canRechnung = user ? canWriteArea(user, 'rechnungen') : false
  const invoiceDoc = invoiceQuery.data?.invoice ?? null

  function festschreiben(): void {
    if (
      !window.confirm(
        'Rechnung verbindlich festschreiben? Es wird eine fortlaufende §14-Rechnungsnummer vergeben; danach ist die Rechnung gesperrt und kann nur noch storniert werden.',
      )
    )
      return
    createInvoice.mutate()
  }

  function stornieren(): void {
    if (
      !window.confirm(
        'Rechnung stornieren? Es wird eine Stornorechnung angelegt (die Originalrechnung bleibt erhalten) und der Auftrag zur Korrektur wieder geöffnet. Danach kannst du eine neue Rechnung festschreiben.',
      )
    )
      return
    storno.mutate()
  }

  // Festgeschrieben → aus dem eingefrorenen Snapshot; sonst Live-Entwurf aus dem aktuellen Auftrag.
  const model =
    invoiced && invoiceDoc
      ? snapshotDocModel(invoiceDoc)
      : liveDocModel({
          kind: 'invoice',
          order,
          tasks: orderQuery.data?.tasks ?? [],
          parts: orderQuery.data?.parts ?? [],
          items: orderQuery.data?.items ?? [],
          billing: settingsQuery.data?.billing ?? null,
          customer: customerQuery.data?.customer ?? null,
          vehicle: customerQuery.data?.vehicles.find((v) => v.id === order.vehicleId) ?? null,
          draft: true,
        })

  return (
    <main className="wf-print" data-theme="light">
      <div className="wf-print__bar">
        <Link to={`/orders/${id}`} className="wf-btn wf-btn--ghost">
          ← Zurück
        </Link>
        <div className="wf-print__baractions">
          {!invoiced && canRechnung ? (
            <button type="button" className="wf-btn" disabled={createInvoice.isPending} onClick={festschreiben}>
              Rechnung festschreiben
            </button>
          ) : null}
          {invoiced && canRechnung ? (
            <button type="button" className="wf-btn wf-btn--ghost" disabled={storno.isPending} onClick={stornieren}>
              Rechnung stornieren
            </button>
          ) : null}
          <button type="button" className="wf-btn wf-btn--primary" onClick={() => window.print()}>
            Drucken / als PDF
          </button>
        </div>
      </div>

      {createInvoice.error instanceof ApiError ? (
        <p className="error" role="alert">
          {createInvoice.error.message}
        </p>
      ) : null}
      {storno.error instanceof ApiError ? (
        <p className="error" role="alert">
          {storno.error.message}
        </p>
      ) : null}

      <BillingDocument model={model} />
    </main>
  )
}
